import React, { useMemo } from "react";
import { Paper, Fade } from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useNavigate } from "react-router-dom";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import CommonButtons from "src/shared/components/CommonButtons";
import { usePaymentTheme } from "../hooks/usePaymentTheme";
import { Order } from "src/shared/services/interfacev2";

interface Props {
  order: Order | null;
}

interface ProductRow {
  id: number;
  productName: string;
  amount: string;
  quantity: number;
  totalPrice: string;
}

const OrderDetailsSection: React.FC<Props> = ({ order }) => {
  const navigate = useNavigate();
  const { fontClass, getBgClass, themeTextColor, getLightBgClass, sidebarThemeClass } = usePaymentTheme();

  /** ✅ Flatten products correctly */
  const rows: ProductRow[] = useMemo(() => {
    if (!order?.orderDetails?.length) return [];

    return order.orderDetails.flatMap((detail) =>
      detail.products.map((p, index) => ({
        id: index + 1,
        productName: p.name || "Unknown Product",
        amount: Number(p.unitPrice || 0).toFixed(2),
        quantity: Number(p.quantity || 0),
        totalPrice: Number(p.lineTotal || 0).toFixed(2),
      })),
    );
  }, [order]);

  const headCells: HeadCell<ProductRow>[] = [
    { id: "id", label: "S.No" },
    { id: "productName", label: "ITEM" },
    { id: "amount", label: "PRICE", numeric: true, align: "right" },
    { id: "quantity", label: "QTY", numeric: true, align: "right" },
    { id: "totalPrice", label: "TOTAL", numeric: true, align: "right" },
  ];

  const status = order?.orders?.[0]?.status?.toLowerCase();

  const orderStatusColor =
    status === "completed"
      ? "text-green-700 bg-green-100 border border-green-300"
      : status === "pending"
        ? "text-yellow-700 bg-[#fff8cc] border border-[#f5da42]"
        : status === "cancelled"
          ? "text-red-700 bg-red-100 border border-red-300"
          : "text-gray-700 bg-gray-100 border border-gray-300";

  return (
    <Fade in={true} timeout={800}>
      <div className={`relative w-full rounded-2xl shadow-lg p-1.5 md:p-2 flex  flex-col h-full ${sidebarThemeClass}}`}>
        <div className="flex items-center gap-3 flex-wrap text-sm md:text-base pb-2 px-1">
          <CommonButtons label="BACK" className="h-8 border border-gray-400 " onClick={() => navigate(-1)} variant="secondary" icon={KeyboardReturnIcon} />

          <div className={`flex items-center justify-center w-[34px] h-[34px] ${getBgClass()} rounded-xl shadow-md`}>
            <ReceiptIcon className="text-white text-[16px]" />
          </div>

          <h2 className={`font-bold text-lg ${themeTextColor}`}>Order #{order?.orders?.[0]?.orderId || "N/A"}</h2>

          <span className={`flex items-center gap-1.5 ${getLightBgClass()} text-gray-800 px-2 py-0.5 rounded-full font-medium`}>
            <LocalDiningIcon className={`text-sm ${themeTextColor}`} />
            Table: {order?.orders?.[0]?.tableNumber ?? "N/A"}
          </span>

          <div className="ml-auto">
            {status && <div className={`px-3 py-1.5 rounded-2xl border font-semibold text-xs md:text-sm tracking-wide whitespace-nowrap ${orderStatusColor}`}>{order?.orders?.[0]?.status}</div>}
          </div>
        </div>

        <div className="thin-scrollbar shadow-inner bg-white overflow-y-auto h-[605px] rounded-xl p-1.5">
          <CommonTable<ProductRow>
            data={rows}
            title=""
            headCells={headCells}
            selectable={false}
            denseToggle={false}
            showToolbar={false}
            pagenation={true}
            showFilterIcon={false}
            pageSize={10}
            className="w-full overflow-y-auto"
          />
        </div>
      </div>
    </Fade>
  );
};

export default OrderDetailsSection;
